import { useEffect, useRef, useState } from 'react'

const getChatbot = () => document.querySelector('alby-chatbot')

const isOpen = chatbot =>
  chatbot?.hasAttribute('is-open') || chatbot?.['is-open'] || false

// Manually dispatch eventListeners since we are not using the default chat bot floating button
const dispatchEvent = (eventName, detail) => {
  const event = new CustomEvent(eventName, { bubbles: true, detail })
  globalThis?.document?.dispatchEvent(event)
}

// This allows us check the current status of the chat window to update the chat bot "isOpen" state
const setupChatbotObserver = (setChatbotIsOpen, chatbot) => {
  const observer = new MutationObserver(() => setChatbotIsOpen(isOpen(chatbot)))
  observer.observe(chatbot, { attributes: true, attributeFilter: ['is-open'] })
  return observer
}

const disconnectObserver = observer => () => observer.disconnect()

// Initialize chatbot state and observer
const setupChatbotState = (chatbot, setChatbotIsOpen) => {
  setChatbotIsOpen(isOpen(chatbot))
  const observer = setupChatbotObserver(setChatbotIsOpen, chatbot)
  return disconnectObserver(observer)
}

// Check for the chat bot in the DOM
const initializeChatbot = setChatbotIsOpen => {
  const chatbot = getChatbot()
  return chatbot ? setupChatbotState(chatbot, setChatbotIsOpen) : undefined
}

// When the chat bot has rendered, dispatch the "rendered" event listener
// Manually dispatch eventListeners since we are not using the default chat bot floating button
const useChatbotEvents = widgetId => {
  const [chatbotIsOpen, setChatbotIsOpen] = useState(false)
  const hasRendered = useRef(false)

  useEffect(() => {
    return initializeChatbot(setChatbotIsOpen)
  }, [])

  useEffect(() => {
    if (!hasRendered.current && widgetId) {
      dispatchEvent('alby:Chatbot::LauncherButtonRendered', { widgetId })
      hasRendered.current = true
    }
  }, [widgetId])

  return chatbotIsOpen
}

// Handles hiding or showing the chat bot using the provided "hide" and "show" functions from the alby-chatbot web component
const handleChatbotClick = (chatbot, widgetId) => {
  const currentState = isOpen(chatbot)
  dispatchEvent('alby:Chatbot::LauncherButtonClicked', {
    isOpen: !currentState,
    widgetId
  })
  currentState ? chatbot.hide() : chatbot.show()
}

const handlePromoBanner = (isPromoBannerVisible, onCloseBanner) => {
  if (isPromoBannerVisible) {
    onCloseBanner()
  }
}

// Handle the main click logic
const handlePromoBannerClick = (
  isPromoBannerVisible,
  onCloseBanner,
  widgetId
) => {
  handlePromoBanner(isPromoBannerVisible, onCloseBanner)

  const chatbot = getChatbot()
  if (chatbot?.show) {
    handleChatbotClick(chatbot, widgetId)
  }
}

const onClick = (isPromoBannerVisible, onCloseBanner, widgetId) => event => {
  event.preventDefault()
  handlePromoBannerClick(isPromoBannerVisible, onCloseBanner, widgetId)
}

export { useChatbotEvents, onClick }
